import { api } from "./client";

// Гласови поръчки — две заявки към BE: записът от микрофона става текст
// (POST /voice/transcribe), а текстът се парсва до поръчка (POST /voice/parse-order).

export interface VoiceProductIn {
  id: number;
  name: string;
  modifierIds: number[];
  addonIds: number[];
}

export interface VoiceModifierIn {
  id: number;
  name: string;
}

export interface VoiceAddonOptionIn {
  itemId: number;
  name: string;
}

export interface VoiceAddonIn {
  id: number;
  name: string;
  // "1" по избор · "2" задължителна · "3" препоръчителна
  type: string;
  options: VoiceAddonOptionIn[];
}

export interface VoiceModifierChoiceOut {
  // Как е казан атрибутът („да е малка") — за показване при избор
  spokenText: string;
  // Кандидати по вероятност: 1 = сигурно, няколко = избор, празно = липса
  modifierIds: number[];
}

export interface VoiceAddonOptionRefOut {
  addonId: number;
  itemId: number;
}

export interface VoiceAddonChoiceOut {
  spokenText: string;
  quantity: number;
  options: VoiceAddonOptionRefOut[];
}

export interface VoiceOrderItemOut {
  // Как е казан продуктът в транскрипта (за показване при избор/грешка)
  spokenName: string;
  quantity: number;
  // Кандидати по вероятност: 1 = сигурно съвпадение, няколко = избор, празно = липса
  productIds: number[];
  modifiers: VoiceModifierChoiceOut[];
  addons: VoiceAddonChoiceOut[];
  // Свободен коментар към продукта („с коментар за бара" → „за бара")
  comment: string;
}

export interface ParseVoiceOrderOut {
  tableName: string | null;
  items: VoiceOrderItemOut[];
  // „Настани резервацията на Здравко" → „Здравко" (тогава `items` е празен);
  // иначе null. Липсва при по-стар BE → поръчка, както досега.
  seatGuestName?: string | null;
}

// Машинните кодове, с които BE-то отговаря при 502 (`detail.code`) —
// огледало на `VoiceParseError` в app/features/voice_orders/service.py.
// Нов код там ⇒ нов ред тук + ключ в bg.ts И en.ts.
const VOICE_ERROR_KEYS: Record<string, string> = {
  too_long: "ordering.voiceErrTooLong",
  unreadable: "ordering.voiceErrUnreadable",
  empty: "ordering.voiceErrEmpty",
  refused: "ordering.voiceErrRefused",
  upstream: "ordering.voiceErrUpstream",
  not_configured: "ordering.voiceErrNotConfigured",
  // Кодовете на /voice/transcribe (502/400/413/415 — статусът не е важен,
  // кодът е контрактът)
  no_speech: "ordering.voiceNoSpeech",
  audio_too_large: "ordering.voiceErrAudioTooLarge",
  unsupported_audio: "ordering.voiceErrUnsupportedAudio",
};

/** i18n ключът за провалила се гласова заявка.
 *
 * Текстът се сглобява във FE-то, за да е на езика на терминала — BE-то праща
 * само код. Непознат код (по-ново BE срещу по-старо FE) пада към общото
 * съобщение; НИКОГА не показваме `detail.message`, той е само български.
 */
export const voiceErrorKey = (error: unknown): string => {
  const detail = (
    error as { response?: { data?: { detail?: unknown } } }
  )?.response?.data?.detail;
  const code =
    detail && typeof detail === "object"
      ? (detail as { code?: unknown }).code
      : undefined;
  return (
    (typeof code === "string" ? VOICE_ERROR_KEYS[code] : undefined) ??
    "ordering.voiceParseError"
  );
};

export const parseVoiceOrder = async (
  transcript: string,
  products: VoiceProductIn[],
  modifiers: VoiceModifierIn[],
  addons: VoiceAddonIn[],
): Promise<ParseVoiceOrderOut> => {
  const response = await api.post<ParseVoiceOrderOut>("/voice/parse-order", {
    transcript,
    products,
    modifiers,
    addons,
  });
  return response.data;
};

// Разширението не е важно за BE-то (то гледа MIME-а на частта), но
// multipart частта трябва да носи име на файл.
const AUDIO_FILENAME_BY_MIME: Record<string, string> = {
  "audio/webm": "voice.webm",
  "audio/mp4": "voice.mp4",
  "audio/ogg": "voice.ogg",
};

/** Записът от микрофона → текст. multipart/form-data: `audio` = blob-ът на
 * MediaRecorder с неговия MIME (`audio/webm` от Chrome/Android/Firefox,
 * `audio/mp4` от Safari/iOS) + по едно поле `keywords` за всяко име от
 * менюто — подсказка към модела за правописа („тара муселата" →
 * „тарамосалата"), не задължителен изход. Content-Type НЕ се задава ръчно —
 * браузърът слага boundary-то. Резултатът се подава на parseVoiceOrder
 * непроменен.
 */
export const transcribeVoice = async (
  audio: Blob,
  mimeType: string,
  keywords: string[],
): Promise<string> => {
  // blob-ът вече носи `type` = mimeType (useAudioRecorder го създава така)
  const form = new FormData();
  form.append("audio", audio, AUDIO_FILENAME_BY_MIME[mimeType] ?? "voice.bin");
  for (const keyword of keywords) form.append("keywords", keyword);
  const response = await api.post<{ transcript: string }>(
    "/voice/transcribe",
    form,
  );
  return response.data.transcript;
};
